import { useCallback, useState, type FC } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material'

import { DialogHeader } from '@/components/atoms/DialogHeader'
import { MetaText } from '@/components/atoms/MetaText'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  NOTION_BUTTON_PRIMARY_SX,
  NOTION_BUTTON_TEXT_SX,
} from '@/shared/styles/buttonStyles'
import {
  NOTION_DIALOG_PAPER_SX,
  NOTION_DIALOG_TEXTFIELD_SX,
} from '@/shared/styles/dialogStyles'

// back の serializer と同じ上限 (超えると 400)。欄でも止めて、黙って切らない。
const NOTE_MAX_LENGTH = 500

interface Props {
  open: boolean
  onClose: () => void
  // 送信に成功したら resolve、失敗したら reject する。文章は dialog が持ったままにする。
  onSubmit: (note: string) => Promise<void>
}

// 改善依頼 (= この生成は変だった、を当社へ一言で送る)。欄は 1 つだけで、節も選ばせない。
// 生成の身元 (request_id) は呼び出し側が持っているので画面には出さない (yakureki-back log/231 D2)。
export const FeedbackDialog: FC<Props> = ({ open, onClose, onSubmit }) => {
  const [note, setNote] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  // 閉じるときに状態を初期化する (同フォルダの PrescriptionReadingDialog と同じ作法)。
  // 画面に状態を残さない機能なので、書きかけも持ち越さない — 持ち越すと、別の記録を開いた
  // ときに前の記録について書いた文章がそのまま次の記録に送られる。
  const handleClose = useCallback(() => {
    if (isSending) return
    setNote('')
    setHasFailed(false)
    onClose()
  }, [isSending, onClose])

  const handleSubmit = async () => {
    const trimmed = note.trim()
    if (!trimmed || isSending) return
    setIsSending(true)
    setHasFailed(false)
    try {
      await onSubmit(trimmed)
      setNote('')
      onClose()
    } catch {
      // 送れなかったときは閉じない。書いた文章をそのまま残して、もう一度押せるようにする。
      setHasFailed(true)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSending}
      sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
    >
      <DialogHeader
        title="改善依頼"
        onClose={handleClose}
        disabled={isSending}
      />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <TextField
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
          fullWidth
          multiline
          minRows={4}
          disabled={isSending}
          placeholder="気になったところ（例: A の 2 行目が会話に無い・薬の名前がちがう）"
          inputProps={{ maxLength: NOTE_MAX_LENGTH }}
          sx={NOTION_DIALOG_TEXTFIELD_SX}
        />
        {hasFailed && (
          <MetaText sx={{ mt: 1, color: colorTokens.state.warnFg }}>
            送れませんでした
          </MetaText>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="text"
          size="small"
          disabled={isSending}
          sx={NOTION_BUTTON_TEXT_SX}
        >
          キャンセル
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          size="small"
          disabled={!note.trim() || isSending}
          sx={NOTION_BUTTON_PRIMARY_SX}
        >
          {isSending ? '送信中…' : '送信'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
